import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Draggable } from 'gsap/Draggable';
import { parallaxAnim, summaryAnim } from '../animations';
import loadVideos from '../videos';
import Splide from '@splidejs/splide';

gsap.registerPlugin(ScrollTrigger, Draggable);

// Detect Safari and add class to body for specific styling
if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
    document.body.classList.add('safari');
}

const bonusAnim = () => {
    const portrait = document.querySelectorAll('.bonus .bonus__phone');

    portrait.forEach((iphone) => {
        gsap.from(iphone, {
            y: 100,
            ease: 'none',
            scrollTrigger: {
                trigger: iphone,
                start: 'top bottom',
                end: 'top center',
                scrub: true,
            },
        });
    });
};

const compareAnim = () => {
    const blocks = document.querySelectorAll('.compare-two');
    const style = document.getElementById('dynamic-styles');

    blocks.forEach((block, index) => {
        const before = block.querySelector('.compare-after');
        const handle = block.querySelector('.twentytwenty-handle');
        const start = Math.floor(block.clientWidth * (block.dataset.start / 100));
        const end = Math.floor(block.clientWidth * (block.dataset.end / 100));
        const speed = block.dataset.speed ?? 4;
        const animationName = `compare-anim-${index}`;
        const animationNameHandle = `handle-anim-${index}`;

        style.textContent += `
            @keyframes ${animationName} {
                0% {
                    clip: rect(0px, ${block.clientWidth}px, 100vh, ${end}px)
                }
                50% {
                    clip: rect(0px, ${block.clientWidth}px, 100vh, ${start}px)
                }
                100% {
                    clip: rect(0px, ${block.clientWidth}px, 100vh, ${end}px)
                }
            }
            @keyframes ${animationNameHandle} {
                0% {
                    left: ${end}px;
                }
                50% {
                    left: ${start}px;
                }
                100% {
                    left: ${end}px;
                }
            }
            .${animationName} {
                animation: ${animationName} ${speed}s infinite ;
            }
            .${animationNameHandle} {
                animation: ${animationNameHandle} ${speed}s infinite;
            }
        `;

        block.classList.add('compare-two--anim');
        block.classList.remove('loading');
        before.style.clip = `rect(0px, ${start}px, 100vh, 0px)`;
        before.classList.add(animationName);
        handle.style.left = `${start}px`;
        handle.classList.add(animationNameHandle);

        block.addEventListener(
            'mouseenter',
            (event) => {
                const offset = event.offsetX;

                block.classList.add('compare-two--stop');
                before.style.clip = `rect(0px, ${block.clientWidth}px, 100vh, ${offset}px)`;
                handle.style.left = `${offset}px`;
            },
            false,
        );

        block.addEventListener(
            'touchstart',
            (event) => {
                const offset = event.touches[0].clientX;

                block.classList.add('compare-two--stop');
                before.style.clip = `rect(0px, ${block.clientWidth}px, 100vh, ${offset}px)`;
                handle.style.left = `${offset}px`;
            },
            false,
        );

        block.addEventListener(
            'mousemove',
            (event) => {
                const offset = event.offsetX;

                before.style.clip = `rect(0px, ${block.clientWidth}px, 100vh, ${offset}px)`;
                handle.style.left = `${offset}px`;
            },
            false,
        );

        block.addEventListener(
            'touchmove',
            (event) => {
                const offset = event.touches[0].clientX;

                before.style.clip = `rect(0px, ${block.clientWidth}px, 100vh, ${offset}px)`;
                handle.style.left = `${offset}px`;
            },
            false,
        );

        ['mouseleave', 'touchend'].forEach((evt) => {
            block.addEventListener(
                evt,
                () => {
                    block.classList.remove('compare-two--stop');
                },
                false,
            );
        });
    });
};

const fancyAnim = () => {
    // Skip animation in Crowdin environment to prevent text rendering issues
    if (window.App && (window.App.isCrowdin || window.App.appEnvironment === 'l10n')) {
        return;
    }

    const headings = document.querySelectorAll('.fancy');

    headings.forEach((heading) => {
        const isRTL = window.App?.isRTL || false;

        if (isRTL) {
            // For Arabic/RTL: animate word by word instead of letter by letter
            const words = heading.textContent.trim().split(/\s+/);

            const content = words.map((word, i) => {
                // Staggered delay for a smooth wave effect
                const delay = i * 250 + Math.floor(Math.random() * 200 + 1);
                return '<span class="arabic-word" style="transition-delay: ' + delay + 'ms">' + word + '</span>';
            });

            heading.innerHTML = content.join(' ');
        } else {
            // Original letter-by-letter animation for non-Arabic text
            const letters = heading.textContent.split('');

            const content = letters.map((val, i) => {
                const delay = Math.floor(Math.random() * 500 + 1);
                return '<span style="transition-delay: ' + delay + 'ms">' + val + '</span>';
            });

            heading.innerHTML = '';

            for (var i = 0; i < content.length; i++) {
                heading.innerHTML += content[i];
            }
        }

        ScrollTrigger.create({
            trigger: heading,
            start: 'top bottom+=200',
            onEnter: () => {
                heading.classList.add('fancy--anim');
            },
        });
    });
};

const compareScroll = () => {
    const compareBlocks = document.querySelectorAll('.compare-scroll');
    compareBlocks.forEach((compareBlock) => {
        const nest = compareBlock.querySelector('.compare-scroll__nest');
        const thumbnails = compareBlock.querySelectorAll('.compare-scroll__thumbnail');
        const thumbnailsNest = compareBlock.querySelector('.compare-scroll__thumbnails');
        const images = compareBlock.querySelectorAll('.compare-scroll__image');
        let stopAnim = false;
        let prevIndex = 0;
        let currentImageIndex = 0;
        let timer;

        const changeImage = (index) => {
            images.forEach((img, idx) => {
                img.classList.remove('active', 'prev');

                if (idx === prevIndex) {
                    img.classList.add('prev');
                }
                if (idx === index) {
                    img.classList.add('active');
                    setTimeout(() => (prevIndex = idx), 100);
                }
            });

            thumbnails.forEach((thumb, idx) => {
                thumb.classList.remove('active');
                if (idx === index) {
                    thumb.classList.add('active');
                }
            });

            currentImageIndex = index;
        };

        const autoSwitchImage = () => {
            if (stopAnim) return;
            currentImageIndex = (currentImageIndex + 1) % images.length;
            changeImage(currentImageIndex);
        };

        const resetTimer = () => {
            clearInterval(timer);
            timer = setInterval(autoSwitchImage, 1000);
        };

        thumbnailsNest.addEventListener('mouseenter', () => {
            stopAnim = true;
        });

        thumbnailsNest.addEventListener('mouseleave', () => {
            stopAnim = false;
        });

        thumbnails.forEach((thumbnail, index) => {
            thumbnail.addEventListener('click', () => {
                changeImage(index);
                resetTimer();
            });
        });

        // Create a ScrollTrigger for each compare block
        ScrollTrigger.create({
            trigger: nest,
            start: 'bottom bottom',
            onEnter: () => {
                changeImage(1);
                resetTimer();
            },
        });
    });
};

const horizontalLoop = () => {
    const sliders = document.querySelectorAll('.splide');

    sliders.forEach((slider) => {
        const splide = new Splide(slider, {
            type: 'loop',
            perMove: 1,
            perPage: 5,
            pauseOnHover: false,
            pauseOnFocus: false,
            speed: 600,
            arrows: false,
            drag: false,
            pagination: false,
            autoplay: true,
            interval: 2000,
            updateOnMove: true,
            focus: 'center',
            easing: 'ease-in-out',
        });

        splide.mount();
    });
};

window.addEventListener('load', () => {
    loadVideos();
    compareScroll();
    bonusAnim();
    fancyAnim();
    compareAnim();
    parallaxAnim();
    summaryAnim();
    horizontalLoop();
});

function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

window.addEventListener(
    'resize',
    debounce(() => {
        compareAnim();
    }, 200),
);
